import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

interface Transaction {
  id: string;
  type: 'income' | 'expense';
  category: string;
  description: string;
  amount: number;
  transaction_date: string;
  status: 'pending' | 'completed' | 'cancelled';
  payment_method?: string;
  reference_number?: string;
  notes?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  tournamentId: string;
  transactionType: 'income' | 'expense';
  transaction?: Transaction | null;
}

const INCOME_CATEGORIES = [
  { value: 'sponsorship', label: 'Sponsorship' },
  { value: 'registration', label: 'Registration Fees' },
  { value: 'merchandise', label: 'Merchandise Sales' },
  { value: 'ticket', label: 'Ticket Sales' },
  { value: 'donation', label: 'Donations' },
  { value: 'other', label: 'Other Income' },
];

const EXPENSE_CATEGORIES = [
  { value: 'venue', label: 'Venue & Facilities' },
  { value: 'prize', label: 'Prize Money' },
  { value: 'marketing', label: 'Marketing & PR' },
  { value: 'operations', label: 'Staff & Operations' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'travel', label: 'Travel & Accommodation' },
  { value: 'catering', label: 'Catering & Hospitality' },
  { value: 'other', label: 'Other Expenses' },
];

interface FormState {
  category: string;
  description: string;
  amount: string;
  transaction_date: string;
  status: string;
  payment_method: string;
  reference_number: string;
  notes: string;
}

const emptyForm = (type: 'income' | 'expense'): FormState => ({
  category: '',
  description: '',
  amount: '',
  transaction_date: new Date().toISOString().split('T')[0],
  status: 'pending',
  payment_method: '',
  reference_number: '',
  notes: '',
});

export default function MobileTransactionSheet({ isOpen, onClose, tournamentId, transactionType, transaction }: Props) {
  const [form, setForm] = useState<FormState>(emptyForm(transactionType));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setError('');
      if (transaction) {
        setForm({
          category: transaction.category,
          description: transaction.description,
          amount: String(transaction.amount),
          transaction_date: transaction.transaction_date,
          status: transaction.status,
          payment_method: transaction.payment_method || '',
          reference_number: transaction.reference_number || '',
          notes: transaction.notes || '',
        });
      } else {
        setForm(emptyForm(transactionType));
      }
    }
  }, [isOpen, transaction, transactionType]);

  const f = (key: keyof FormState, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) { setError('Amount must be greater than 0'); return; }
    if (!form.category) { setError('Category is required'); return; }
    if (!form.description.trim()) { setError('Description is required'); return; }

    try {
      setSaving(true);
      const payload = {
        type: transactionType,
        category: form.category,
        description: form.description.trim(),
        amount,
        transaction_date: form.transaction_date,
        status: form.status as 'pending' | 'completed' | 'cancelled',
        payment_method: form.payment_method || null,
        reference_number: form.reference_number || null,
        notes: form.notes || null,
      };

      if (transaction) {
        const { error: err } = await supabase
          .from('financial_transactions')
          .update({ ...payload, updated_at: new Date().toISOString() })
          .eq('id', transaction.id);
        if (err) throw err;
        toast.success('Transaction updated');
      } else {
        const { error: err } = await supabase
          .from('financial_transactions')
          .insert([{ ...payload, tournament_id: tournamentId }]);
        if (err) throw err;
        toast.success('Transaction added');
      }
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save transaction');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const field = 'w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50';
  const label = 'block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide';
  const isIncome = transactionType === 'income';
  const categories = isIncome ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
  const accentColor = isIncome ? 'bg-green-500' : 'bg-red-500';

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-t-3xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-golf-gray">
              {transaction ? 'Edit' : 'Add'} {isIncome ? 'Revenue' : 'Expense'}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">{isIncome ? 'Income transaction' : 'Expense transaction'}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 active:bg-gray-200 transition-colors">
            <XMarkIcon className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSave} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">{error}</div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Category *</label>
              <select value={form.category} onChange={e => f('category', e.target.value)} className={field}>
                <option value="">Select...</option>
                {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Status *</label>
              <select value={form.status} onChange={e => f('status', e.target.value)} className={field}>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div>
            <label className={label}>Description *</label>
            <input type="text" value={form.description} onChange={e => f('description', e.target.value)} required className={field} placeholder="Transaction description..." />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Amount (₦) *</label>
              <input type="number" value={form.amount} onChange={e => f('amount', e.target.value)} required step="0.01" min="0.01" className={field} placeholder="0.00" />
            </div>
            <div>
              <label className={label}>Date *</label>
              <input type="date" value={form.transaction_date} onChange={e => f('transaction_date', e.target.value)} required className={field} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Payment Method</label>
              <input type="text" value={form.payment_method} onChange={e => f('payment_method', e.target.value)} className={field} placeholder="e.g. Bank transfer" />
            </div>
            <div>
              <label className={label}>Reference No.</label>
              <input type="text" value={form.reference_number} onChange={e => f('reference_number', e.target.value)} className={field} placeholder="Optional" />
            </div>
          </div>

          <div>
            <label className={label}>Notes</label>
            <textarea value={form.notes} onChange={e => f('notes', e.target.value)} rows={3} className={`${field} resize-none`} placeholder="Optional notes..." />
          </div>

          <div className="pb-2" />
        </form>

        <div className="flex gap-3 px-5 pt-4 pb-20 border-t border-gray-100 flex-shrink-0">
          <button type="button" onClick={onClose} className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 bg-white active:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button type="submit" onClick={handleSave} disabled={saving} className={`flex-1 py-3 rounded-xl text-white text-sm font-semibold disabled:opacity-60 transition-colors ${accentColor}`}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
